import { BlockPlaybook } from '@driftt/api-types'
import { Button, IconButton, Nav, ToolTip } from '@driftt/tide-core'
import { showTransientNotification } from '@driftt/traffic-control'
import { pluralize } from 'humanize-plus'
import { isEmpty, pathOr } from 'ramda'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { exportBlockCopy, savePlaybookFormattedBlock } from 'api/blocks'
import PlaybookErrorsNotifier from 'components/PlaybookErrorsNotifier'
import { hasExportBotCopy } from 'utils/access'
import createZipDownload from 'utils/createZipDownload'
import { isEmptyOrNil } from 'utils/index'
import { registerContentVariables } from 'utils/registerContentVariables'
import { getMajorVersionFromBlock } from 'utils/validate'

import styles from './styles.module.css'

const registerBlockContentVariables = (blockPb: BlockPlaybook) => {
  const { skills } = blockPb.actions[0].action

  if (!isEmptyOrNil(skills)) {
    skills.forEach((skill) => registerContentVariables(skill, blockPb, false))
  }
}

interface SubNavProps {
  hasChanges: boolean
  stateRefresh: (updatedBlock: BlockPlaybook) => void
  block: BlockPlaybook
  onBlockMajorVersionUpdate: () => void
  numConsumingPlaybooks: number
  numOutdatedPlaybooks: number
}

const BlockSubNav = ({
  block,
  hasChanges,
  stateRefresh,
  onBlockMajorVersionUpdate,
  numConsumingPlaybooks,
  numOutdatedPlaybooks,
}: SubNavProps) => {
  const canExportBotCopy = hasExportBotCopy()
  const [savingChanges, setSavingChanges] = useState(false)
  const dispatch = useDispatch()

  const playbookErrors = useSelector(
    pathOr({}, ['__PLAYBOOK_EDITOR', 'playbookValidation', 'playbook'])
  )

  const getNumPlaybookString = (numPlaybooks: number) =>
    `${numPlaybooks} ${pluralize(numPlaybooks, 'playbook')}`

  const getSuccessMessage = (isMajorVersion: boolean) => {
    if (isMajorVersion && numConsumingPlaybooks) {
      return `Block saved. Updates required in ${getNumPlaybookString(
        numConsumingPlaybooks
      )}.`
    }

    if (numOutdatedPlaybooks) {
      return `Block saved. Updates required in ${getNumPlaybookString(
        numOutdatedPlaybooks
      )}.`
    }

    if (numConsumingPlaybooks) {
      return `Block saved and updated in ${getNumPlaybookString(
        numConsumingPlaybooks
      )}.`
    }

    return `Block successfully saved.`
  }

  const handleBlockPublish = () => {
    setSavingChanges(true)
    let isMajorVersion = false
    savePlaybookFormattedBlock(block)
      .then(({ data }) => {
        registerBlockContentVariables(block)
        if (
          getMajorVersionFromBlock(block) !== getMajorVersionFromBlock(data)
        ) {
          onBlockMajorVersionUpdate()
          isMajorVersion = true
        }
        stateRefresh(data)
        dispatch(
          showTransientNotification({
            type: 'success',
            message: getSuccessMessage(isMajorVersion),
          })
        )
      })
      .catch((error) => {
        console.warn(error)
        dispatch(
          showTransientNotification({
            type: 'error',
            message: 'There was an error saving your block.',
          })
        )
      })
      .finally(() => {
        setSavingChanges(false)
      })
  }

  async function downloadPlaybookCopyToZip() {
    try {
      const {
        meta: { blockId },
      } = block
      const { data } = await exportBlockCopy(blockId)
      createZipDownload(data)
    } catch (err) {
      console.warn(err)
    }
  }

  return (
    <Nav className={styles.blockSubNav}>
      <PlaybookErrorsNotifier />
      {isEmpty(playbookErrors) ? (
        <Button
          disabled={!hasChanges}
          className={styles.blockSubNavPublishButton}
          onClick={handleBlockPublish}
          loading={savingChanges}
        >
          Save changes
        </Button>
      ) : (
        <ToolTip flow="up" component="Fix block errors before saving">
          <Button disabled={true} className={styles.blockSubNavPublishButton}>
            Save changes
          </Button>
        </ToolTip>
      )}
      {canExportBotCopy && (
        <ToolTip render={() => 'Download bot text'} flow="left">
          <IconButton
            className="playbook-editor-subnav--download"
            icon="download-2"
            onClick={downloadPlaybookCopyToZip}
          />
        </ToolTip>
      )}
    </Nav>
  )
}

export default BlockSubNav
